import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { RootState } from "../store";
import {
  fetchProductsThunk,
  updateFilters,
  clearFilters,
} from "../store/slices/productsSlice";
import {
  addToCart,
  removeFromCart,
  updateCartItem,
} from "../store/slices/cartSlice";
import toast from "react-hot-toast";

const PRODUCT_CATEGORIES = [
  { label: "All", value: "" },
  { label: "Vegetables", value: "vegetables" },
  { label: "Fruits", value: "fruits" },
  { label: "Grains", value: "grains" },
  { label: "Livestock", value: "livestock" },
  { label: "Dairy", value: "dairy" },
  { label: "Herbs", value: "herbs" },
  { label: "Nuts", value: "nuts" },
];

const WHATSAPP_NUMBER = "2348123456789";
const PHONE_NUMBER = "+234 812 345 6789";

const Products: React.FC = () => {
  const dispatch = useDispatch();
  const { products, isLoading, error, filters } = useSelector(
    (state: RootState) => state.products
  );
  const cart = useSelector((state: RootState) => state.cart);
  const { currentTenant } = useSelector((state: RootState) => state.tenant);

  // Local state for search debounce
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const [cartOpen, setCartOpen] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState<"delivery" | "pickup">(
    "delivery"
  );
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchInput !== filters.search) {
        dispatch(updateFilters({ search: searchInput }));
      }
    }, 400);
    return () => clearTimeout(handler);
    // eslint-disable-next-line
  }, [searchInput]);

  // Fetch products when filters or tenant change
  useEffect(() => {
    if (currentTenant?.id) {
      const params: any = {
        category: filters.category || undefined,
        search: filters.search || undefined,
        is_available: true,
      };
      dispatch(
        fetchProductsThunk({ tenantId: currentTenant.id, params }) as any
      );
    }
  }, [dispatch, currentTenant, filters.category, filters.search]);

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    dispatch(updateFilters({ category: e.target.value }));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    setSearchInput("");
  };

  const getQuantity = (productId: string) => quantities[productId] || 1;

  const setQuantity = (productId: string, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [productId]: Math.max(1, quantity),
    }));
  };

  const handleAddToCart = (product: any) => {
    const quantity = getQuantity(product.id);
    dispatch(
      addToCart({
        product_id: product.id,
        name: product.name,
        unit_price: product.price_per_unit,
        quantity,
        unit: product.unit,
        product_image: product.product_images?.[0],
      })
    );
    toast.success(`${product.name} added to cart`);
  };

  const cartCount = useMemo(
    () => cart.items.reduce((sum, item) => sum + item.quantity, 0),
    [cart.items]
  );
  const deliveryFee = deliveryMethod === "delivery" ? 500 : 0;
  const orderTotal = cart.total + deliveryFee;

  const handleWhatsAppOrder = (product: any) => {
    const msg = encodeURIComponent(
      `Hello, I want to order the following product:\n\nProduct: ${
        product.name
      }\nPrice: ₦${product.price_per_unit.toLocaleString()} per ${
        product.unit
      }\nPlease provide a quote and availability.`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
  };

  const handleCall = () => {
    navigator.clipboard.writeText(PHONE_NUMBER);
    toast.success("Phone number copied! Call us to place your order.");
  };

  return (
    <div className="space-y-6 relative">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Products
        </h1>
        <div className="flex items-center gap-2">
          <button className="btn-primary">Add Product</button>
          <button
            onClick={() => setCartOpen((prev) => !prev)}
            className="relative border border-green-500 text-green-700 px-4 py-2 rounded-lg text-sm hover:bg-green-50 transition-colors"
          >
            Cart
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs rounded-full px-2 py-0.5">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>
      {/* Filters Bar */}
      <div className="flex flex-col md:flex-row gap-4 items-center bg-white rounded-lg shadow p-4">
        <select
          className="border rounded px-3 py-2 text-gray-700"
          value={filters.category}
          onChange={handleCategoryChange}
        >
          {PRODUCT_CATEGORIES.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="border rounded px-3 py-2 flex-1 min-w-[200px]"
          placeholder="Search products..."
          value={searchInput}
          onChange={handleSearchChange}
        />
        {searchInput && (
          <button
            className="text-sm text-gray-500 hover:text-gray-700"
            onClick={() => setSearchInput("")}
          >
            Clear search
          </button>
        )}
        <button
          className="text-sm text-gray-500 hover:text-green-600 underline"
          onClick={handleClearFilters}
        >
          Clear Filters
        </button>
      </div>
      {isLoading && (
        <div className="text-center py-12 text-green-600 font-semibold">
          Loading products...
        </div>
      )}
      {error && (
        <div className="text-center py-12 text-red-600 font-semibold">
          {error}
        </div>
      )}
      {!isLoading && !error && products.length === 0 && (
        <div className="card">
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No products yet
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              Start by adding your first product to the marketplace
            </p>
            <button className="btn-primary">Add Your First Product</button>
          </div>
        </div>
      )}
      {!isLoading && !error && products.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 p-4 flex flex-col"
            >
              <div className="relative h-48 bg-gray-100 rounded-xl mb-4 flex items-center justify-center overflow-hidden">
                {product.product_images && product.product_images.length > 0 ? (
                  <img
                    src={product.product_images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover rounded-xl hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="text-4xl text-green-400">🥕</div>
                )}
                <div className="absolute top-2 left-2 flex gap-2">
                  {product.is_featured && (
                    <span className="text-xs font-semibold bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                      Featured
                    </span>
                  )}
                  {product.current_stock < 10 && (
                    <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-1 rounded-full">
                      Low stock
                    </span>
                  )}
                </div>
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-2">
                {product.name}
              </h3>
              <div className="text-gray-600 mb-2 line-clamp-2">
                {product.description}
              </div>
              <div className="text-xs text-gray-500 mb-2">
                Seller: {product.seller_name || "AgriFlow Partner Farm"}
              </div>
              <div className="text-xs text-gray-500 mb-3">
                Available: {product.current_stock} {product.unit}
              </div>
              <div className="flex items-center justify-between mt-auto mb-2">
                <span className="text-green-700 font-semibold text-lg">
                  ₦{product.price_per_unit.toLocaleString()}
                </span>
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                  {product.unit}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <button
                  className="w-8 h-8 border rounded-lg text-gray-700"
                  onClick={() =>
                    setQuantity(product.id, Math.max(1, getQuantity(product.id) - 1))
                  }
                >
                  -
                </button>
                <input
                  type="number"
                  min={1}
                  value={getQuantity(product.id)}
                  onChange={(e) =>
                    setQuantity(product.id, Number(e.target.value) || 1)
                  }
                  className="w-16 text-center border rounded-lg py-1"
                />
                <button
                  className="w-8 h-8 border rounded-lg text-gray-700"
                  onClick={() => setQuantity(product.id, getQuantity(product.id) + 1)}
                >
                  +
                </button>
                <Link
                  to={`/app/products/${product.id}`}
                  className="ml-auto text-sm text-green-700 hover:underline"
                >
                  Quick view
                </Link>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                <button
                  className="btn-primary text-sm py-2"
                  onClick={() => handleAddToCart(product)}
                >
                  Add to Cart
                </button>
                <button
                  className="border border-green-500 text-green-700 px-4 py-2 rounded-lg text-sm hover:bg-green-50 transition-colors"
                  onClick={() => handleWhatsAppOrder(product)}
                >
                  Order via WhatsApp
                </button>
                <button
                  className="border border-green-500 text-green-700 px-4 py-2 rounded-lg text-sm hover:bg-green-50 transition-colors"
                  onClick={handleCall}
                >
                  Call to Order
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {cartCount > 0 && (
        <button
          onClick={() => setCartOpen(true)}
          className="fixed bottom-5 right-5 bg-green-600 text-white rounded-full shadow-lg px-5 py-3 font-semibold z-30 hover:bg-green-700 transition-colors"
        >
          {cartCount} item{cartCount > 1 ? "s" : ""} in cart
        </button>
      )}

      {cartOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <button
            className="absolute inset-0 bg-black/30"
            onClick={() => setCartOpen(false)}
            aria-label="Close cart"
          />
          <div className="relative w-full max-w-md h-full bg-white shadow-2xl p-5 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Your Cart</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setCartOpen(false)}
              >
                Close
              </button>
            </div>

            {cart.items.length === 0 ? (
              <p className="text-gray-500 text-sm">Your cart is empty.</p>
            ) : (
              <div className="space-y-4">
                {cart.items.map((item) => (
                  <div
                    key={item.product_id}
                    className="border border-gray-200 rounded-lg p-3"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{item.name}</h3>
                        <p className="text-sm text-gray-500">
                          ₦{item.unit_price.toLocaleString()} / {item.unit}
                        </p>
                      </div>
                      <button
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => dispatch(removeFromCart(item.product_id))}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        className="w-7 h-7 border rounded"
                        onClick={() =>
                          dispatch(
                            updateCartItem({
                              product_id: item.product_id,
                              quantity: Math.max(1, item.quantity - 1),
                            })
                          )
                        }
                      >
                        -
                      </button>
                      <span className="min-w-[2rem] text-center">{item.quantity}</span>
                      <button
                        className="w-7 h-7 border rounded"
                        onClick={() =>
                          dispatch(
                            updateCartItem({
                              product_id: item.product_id,
                              quantity: item.quantity + 1,
                            })
                          )
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-gray-200 space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Delivery Method
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setDeliveryMethod("delivery")}
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      deliveryMethod === "delivery"
                        ? "border-green-600 bg-green-50 text-green-700"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    Delivery (₦500)
                  </button>
                  <button
                    onClick={() => setDeliveryMethod("pickup")}
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      deliveryMethod === "pickup"
                        ? "border-green-600 bg-green-50 text-green-700"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    Pickup (Free)
                  </button>
                </div>
              </div>

              <div className="text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">₦{cart.total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery fee</span>
                  <span className="font-semibold">₦{deliveryFee.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="font-bold text-green-700">
                    ₦{orderTotal.toLocaleString()}
                  </span>
                </div>
              </div>

              <button
                className="w-full btn-primary py-3"
                onClick={() => toast.success("Checkout flow will open next.")}
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
